/* eslint-disable brace-style */
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnChanges, OnDestroy, OnInit, SimpleChanges, inject } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { AbstractDialogResultData, AbstractFormDialog } from '@next/core-lib/dialog';
import { Store } from '@ngrx/store';
import { debounceTime, Subscription } from 'rxjs';
import {
  CropData,
  FertilizationSettingWithName,
  GrasslandUsage,
  NitrogenCalculationSettingToggles,
  SiteState,
} from 'src/app/shared/models';

import { QuickEditDialogModel } from './edit-all-fertilization-settings-dialog.model';

import { LoadNitrogenCalculationSettingTogglesByAdvices } from '../../store/actions';

@Component({
  selector: 'app-edit-all-fertilization-settings-dialog',
  templateUrl: './edit-all-fertilization-settings-dialog.component.html',
  styleUrls: ['./edit-all-fertilization-settings-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class EditAllFertilizationSettingsDialogComponent
  extends AbstractFormDialog<{ fertilizationSettingsWithName: FertilizationSettingWithName[] }>
  implements OnInit, OnChanges, OnDestroy
{
  data = inject<QuickEditDialogModel>(MAT_DIALOG_DATA);
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly formBuilder = inject(FormBuilder);
  protected mdDialogRef = inject<MatDialogRef<EditAllFertilizationSettingsDialogComponent, AbstractDialogResultData<{
    fertilizationSettingsWithName: FertilizationSettingWithName[];
}>>>(MatDialogRef);
  private readonly cdRef = inject(ChangeDetectorRef);

  searchControl = new FormControl();
  isSearching = false;
  searchValueChangeSubscription = new Subscription();
  searchQuery: string;
  crops: CropData[];
  greenManureCrops: CropData[];
  fertilizationSettingsWithName: FertilizationSettingWithName[] =
    this.selectFertilizationSettingsWithName();
  relevantFertilizationSettings: FertilizationSettingWithName[] =
    this.fertilizationSettingsWithName;
  formGroupByFertilizationSettingId: { [fertilizationSettingId: number]: FormGroup } =
    this.fertilizationSettingsWithName.reduce(
      (a, f) => ({
        ...a,
        [f.id]: this.createFertilizationSettingsFormGroup(f),
      }),
      {},
    );
  nitrogenCalculationSettingToggles: NitrogenCalculationSettingToggles[];
  renderQuickEditFormComponents = false;

  formArray = this.formBuilder.array(Object.values(this.formGroupByFertilizationSettingId));
  formGroup = this.formBuilder.group({
    fertilizationSettingsWithName: this.formArray,
  });
  constructor() {
    super();
  }

  ngOnInit(): void {
    // UGLY: This is not the nicest solution, but very effective. For customers with a lot
    // of cropfields, the quick edit dialog may be VERY slow to render that many form components.
    // For this purpose, we delay the rendering by 50ms and show a loading spinner, so the
    // quick edit dialog can instantly open && the user sees a CSS-animated loading spinner
    // while the UI is frozen due to the large amount of Angular render operations.
    setTimeout(() => {
      this.renderQuickEditFormComponents = true;
      this.cdRef.detectChanges();
    }, 50);

    this.searchValueChangeSubscription.add(
      this.searchControl.valueChanges.pipe(debounceTime(300)).subscribe((query: string) => {
        if (!!query !== this.isSearching) {
          this.isSearching = !!query;
        }
        this.isSearching = !!query;
        this.searchQuery = query;
        this.updateRelevantFertilizationSettings(query);
      }),
    );

    this.loadNitrogenCalculationSettingToggles();
  }

  trackByFertilizationSetting(_: number, fertilizationSetting: FertilizationSettingWithName) {
    return fertilizationSetting.id;
  }

  updateRelevantFertilizationSettings(query: string) {
    query = query?.toLowerCase() ?? '';
    this.relevantFertilizationSettings = query
      ? this.fertilizationSettingsWithName.filter(
        (fertilizationSetting) =>
          fertilizationSetting.cropAdviceName.toString().toLowerCase().indexOf(query) !== -1 ||
            fertilizationSetting.cropName.toString().toLowerCase().indexOf(query) !== -1,
      )
      : this.fertilizationSettingsWithName;
  }

  confirmEditFertilizationSettings(data: {
    fertilizationSettingsWithName: FertilizationSettingWithName[];
  }) {
    const editedFertilizationSettings: FertilizationSettingWithName[] = [];
    data.fertilizationSettingsWithName.forEach((editFertilizationSetting) => {
      const formGroup = this.formGroupByFertilizationSettingId[editFertilizationSetting.id];
      if (formGroup?.dirty) {
        const fertilizationSetting = this.fertilizationSettingsWithName.find(
          (f) => f.id === editFertilizationSetting.id,
        );
        const result = this.getResultFertilizationSetting(
          editFertilizationSetting,
          fertilizationSetting,
        );

        editedFertilizationSettings.push(result);
      }
    });
    this.confirm({ fertilizationSettingsWithName: editedFertilizationSettings });
  }

  getResultFertilizationSetting(
    editFertilizationSetting: FertilizationSettingWithName,
    fertilizationSetting: FertilizationSettingWithName,
  ): FertilizationSettingWithName {
    const resultFertilizationSetting = {
      ...fertilizationSetting,
      ...editFertilizationSetting,
    };
    if (!resultFertilizationSetting.isGreenManure) {
      resultFertilizationSetting.greenManureSettings = null;
    }
    if (!resultFertilizationSetting.isPrecedingCrop) {
      resultFertilizationSetting.precedingCropSettings = null;
    }
    return resultFertilizationSetting;
  }

  closeSearch() {
    this.isSearching = false;
    this.searchControl.setValue('');
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['values']) {
      this.searchControl.enable({ emitEvent: true });
    }
  }

  ngOnDestroy() {
    if (this.searchValueChangeSubscription) {
      this.searchValueChangeSubscription.unsubscribe();
    }
  }

  private createFertilizationSettingsFormGroup(f: FertilizationSettingWithName) {
    return this.formBuilder.group({
      id: f.id,
      cropAdviceName: [f.cropAdviceName],
      cropId: [f.cropId],
      cropName: [f.cropName],
      isPrecedingCrop: [f.isPrecedingCrop],
      isGreenManure: [f.isGreenManure],
      shouldBeUsedInUsageStandard: [f.shouldBeUsedInUsageStandard],
      shouldBeUsedInLegalStandard: [f.shouldBeUsedInLegalStandard],
      shouldUseTransitionalArrangement: [f.shouldUseTransitionalArrangement],
      shouldUseLowInPhosphateArrangement: [f.shouldUseLowInPhosphateArrangement],
      organicMatterSupply: [f.organicMatterSupply],
      greenManureSettings: this.formBuilder.group({
        cropId: [f.greenManureSettings?.cropId],
        seasonGreenManureIncorporated: [f.greenManureSettings?.seasonGreenManureIncorporated],
        successRate: [f.greenManureSettings?.successRate],
        isNitrogenDeliveryAmountSetManually: [
          f.greenManureSettings?.isNitrogenDeliveryAmountSetManually,
        ],
        nitrogenDeliveryAmount: [f.greenManureSettings?.nitrogenDeliveryAmount],
      }),
      precedingCropSettings: this.formBuilder.group({
        cropId: [f.precedingCropSettings?.cropId],
        isNitrogenDeliveryAmountSetManually: [
          f.precedingCropSettings?.isNitrogenDeliveryAmountSetManually,
        ],
        nitrogenDeliveryAmount: [f.precedingCropSettings?.nitrogenDeliveryAmount],
      }),
      nitrogenCalculationSettings: this.formBuilder.group({
        isPreviousCropCorn: [f?.nitrogenCalculationSettings?.isPreviousCropCorn ?? false],
        isPreviousCropNotGrain: [f?.nitrogenCalculationSettings?.isPreviousCropNotGrain ?? false],
        grasslandUsage: [f?.nitrogenCalculationSettings?.grasslandUsage ?? GrasslandUsage.Graze],
        shouldSplitNorm: [f?.nitrogenCalculationSettings?.shouldSplitNorm ?? false],
        isNitrogenDifferentiation: [
          f?.nitrogenCalculationSettings?.isNitrogenDifferentiation ?? false,
        ],
        isAfterTornGrass: [f?.nitrogenCalculationSettings?.isAfterTornGrass ?? false],
        isRecoveryFertilisation: [f?.nitrogenCalculationSettings?.isRecoveryFertilisation ?? false],
      }),
      expectedCropYield: [
        f.expectedCropYield,
        [Validators.required, Validators.min(0), Validators.max(1000000000)],
      ],
    });
  }

  private selectFertilizationSettingsWithName(): FertilizationSettingWithName[] {
    const fertilizationSettings = [...this.data.fertilizationSettingsWithName];

    return fertilizationSettings
      .filter((fertilizationSetting) =>
        this.data.cropFieldAdvices.some(
          (advice) => advice.adviceId === fertilizationSetting.adviceId,
        ),
      )
      .map((item) => ({
        ...item,
        cropAdviceName: `${
          this.data.cropFieldAdvices.find((advice) => advice.adviceId === item.adviceId)?.name
        } (${this.data.cropFieldAdvices
          .find((advice) => advice.adviceId === item.adviceId)
          ?.areaInHectares.toFixed(2)} Ha)`,
        cropName: `${
          this.data.cropFieldAdvices.find((advice) => advice.adviceId === item.adviceId)?.crop.name
        }`,
        cropId: this.getCropId(this.data.cropFieldAdvices.find((advice) => advice.adviceId === item.adviceId)?.crop.id),
      }))
      .sort((a, b) => a.cropAdviceName.localeCompare(b.cropAdviceName));

  }

  private getCropId(id: number): number {
    return this.data.crops.find((crop) => crop.id === id)?.agrifirmCrop.id ?? id;
  }

  private loadNitrogenCalculationSettingToggles() {
    const adviceIds = this.fertilizationSettingsWithName.map((setting) => setting.adviceId);
    this.store.dispatch(new LoadNitrogenCalculationSettingTogglesByAdvices(adviceIds));
  }
}
